import React, { useState, useMemo ,useEffect } from 'react';
import PropTypes from 'prop-types';
import './CommonTable.css';
import {ColumnSortTypes} from "../../componentObjects";

const CommonExpandTable = ({
                         data,
                         columns,
                         tableOptionsContent,
                         getExpandContent, 
                         onRowClick,
                         selected, 
                         clearSelected,
                         itemsPerPage = 10,
                         defaultSortField = '',
                         defaultSortDirection = 'asc'
                     }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState(defaultSortField);
    const [sortDirection, setSortDirection] = useState(defaultSortDirection);
    const [sortType, setSortType] = useState(ColumnSortTypes.ALPHABETICAL);


    useEffect(() => {
            setCurrentPage(1);
    }, [data]);

    // Sort data
    const sortedData = useMemo(() => {
        if (!sortField) return data;

        return [...data].sort((a, b) => {
            if (sortType === ColumnSortTypes.DATE) {
                const aValue = new Date(a[sortField]);
                const bValue = new Date(b[sortField]);

                if (aValue === bValue) return 0;

                const comparison = aValue > bValue ? 1 : -1;
                return sortDirection === 'asc' ? comparison : -comparison;
            }
            else { // numeric or alpha
                const aValue = a[sortField];
                const bValue = b[sortField];

                if (aValue === bValue) return 0;

                const comparison = aValue > bValue ? 1 : -1;
                return sortDirection === 'asc' ? comparison : -comparison;
            }
        });
    }, [data, sortField, sortDirection]);

    // Paginate data
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedData.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedData, currentPage, itemsPerPage]);

    // Calculate total pages
    const totalPages = Math.ceil(data.length / itemsPerPage);

    // Handle sort
    const handleSort = (field, sortType) => {
        clearSelected();
        setSortType(sortType);
        if (field === sortField) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handlePageChange = (page) => {
        clearSelected();
        setCurrentPage(page);
    };
    
    let tableOptions = tableOptionsContent !== null ?
        <div className="alerts-tab-wrapper alerts-tab-wrapper-grey margin-top-10">
            <div className="alerts-tab-label pad-left-45 pad-bottom-5" >Table Options</div>
            <div className={"pad-left-40 margin-5"}>
                {tableOptionsContent}
            </div >
        </div > 
        : null;

    return (
        <div >
            {tableOptions}
            
            <div className={"margin-20-30"}>
                <table className="table-hover width-100p">
                    <thead>
                    <tr className="alerts-table-row">
                        {columns.map((column) => (
                            <th
                                key={column.field}
                                onClick={() => column.sortable !== false && handleSort(column.field, column.sortType)}
                                className={column.sortable !== false ? column.className + ' sortable' : column.className}
                            >
                                {column.header}
                                {sortField === column.field && (
                                    <span className="sort-indicator">
                                        {sortDirection === 'asc' ? ' ▲' : ' ▼'}
                                    </span>
                                )}
                            </th>
                        ))}
                    </tr>
                    </thead>
                <tbody>
                {paginatedData.length > 0 ? (
                    paginatedData.map((row, rowIndex) => ([
                        <tr key={rowIndex} className="alerts-table-row" onClick={() => onRowClick(rowIndex, row)}>
                            {columns.map((column) => (
                                <td key={`${rowIndex}-${column.field}`} className={column.rowClassName}>
                                    {column.render ? column.render(row) : row[column.field]}
                                </td>
                            ))}
                        </tr>,
                            selected === rowIndex && (getExpandContent(rowIndex, row))    
                        ]
                    ))
                ) : (
                    <tr>
                        <td colSpan={columns.length} className="no-data">
                            No data available
                        </td>
                    </tr>
                )}
                </tbody>
            </table>

            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                    >
                        &laquo;
                    </button>

                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        &lsaquo;
                    </button>

                    <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        &rsaquo;
                    </button>

                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                    >
                        &raquo;
                    </button>
                </div>
            )}
            </div>
        </div>
    );
};

CommonExpandTable.propTypes = {
    data: PropTypes.array.isRequired,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            header: PropTypes.string.isRequired,
            render: PropTypes.func,
            sortable: PropTypes.bool,
            sortType: PropTypes.string
        })
    ).isRequired,
    getExpandContent: PropTypes.func,
    onRowClick: PropTypes.func,
    selected: PropTypes.number,
    itemsPerPage: PropTypes.number,
    defaultSortField: PropTypes.string,
    defaultSortDirection: PropTypes.oneOf(['asc', 'desc']),
    tableOptionsContent: PropTypes.element,
    clearSelected: PropTypes.func
};

export default CommonExpandTable;